import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bot } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { useLanguage } from '../../contexts/LanguageContext';

const Footer: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language } = useLanguage();

  const handlePricingClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/');
    setTimeout(() => {
      const pricingSection = document.getElementById('pricing');
      if (pricingSection) {
        pricingSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <footer className="bg-[var(--bg-primary)] border-t border-[var(--border-primary)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center space-x-2 text-[var(--brand-primary)]">
              <Bot className="h-6 w-6 text-[var(--brand-primary)]" />
              <span className="font-semibold text-xl">Invoice-Converter.com</span>
            </div>
            <p className="mt-2 text-[var(--text-tertiary)]">{t('footer.description')}</p>
          </div>
          <div>
            <h3 className="font-semibold mb-4 text-[var(--brand-primary)]">{t('footer.quickLinks')}</h3>
            <ul className="space-y-2">
              <li>
                <Link 
                  to={`/${language}/blog`} 
                  className="text-[var(--text-tertiary)] hover:text-[var(--brand-primary)] transition-colors"
                >
                  {t('nav.blog')}
                </Link>
              </li>
              <li>
                <a 
                  href="#pricing" 
                  onClick={handlePricingClick} 
                  className="text-[var(--text-tertiary)] hover:text-[var(--brand-primary)] transition-colors"
                >
                  {t('nav.pricing')}
                </a>
              </li>
              <li><a href="https://climate.stripe.com/Yb7lx2" target="_blank" rel="noopener noreferrer" className="text-[var(--text-tertiary)] hover:text-[var(--brand-primary)] transition-colors">{t('footer.climateContribution')}</a></li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold mb-4 text-[var(--brand-primary)]">{t('footer.services')}</h3>
            <ul className="space-y-2">
              <li>
                <Link 
                  to={`/${language}/pdf-to-xrechnung`} 
                  className="text-[var(--text-tertiary)] hover:text-[var(--brand-primary)] transition-colors"
                >
                  {t('nav.pdfToXRechnung')}
                </Link>
              </li>
              <li>
                <Link 
                  to={`/${language}/xrechnung-to-pdf`} 
                  className="text-[var(--text-tertiary)] hover:text-[var(--brand-primary)] transition-colors"
                >
                  {t('nav.xrechnungToPdf')}
                </Link>
              </li>
              <li>
                <Link 
                  to={`/${language}/xrechnung-viewer`} 
                  className="text-[var(--text-tertiary)] hover:text-[var(--brand-primary)] transition-colors"
                >
                  {t('nav.xrechnungViewer')}
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold mb-4 text-[var(--brand-primary)]">{t('footer.legal')}</h3>
            <ul className="space-y-2">
              <li>
                <Link 
                  to={`/${language}/imprint`} 
                  className="text-[var(--text-tertiary)] hover:text-[var(--brand-primary)] transition-colors"
                >
                  {t('footer.imprint')}
                </Link>
              </li>
              <li>
                <Link 
                  to={`/${language}/privacy-policy`} 
                  className="text-[var(--text-tertiary)] hover:text-[var(--brand-primary)] transition-colors"
                >
                  {t('footer.privacyPolicy')}
                </Link>
              </li>
              <li>
                <Link
                  to={`/${language}/dpa`}
                  className="text-[var(--text-tertiary)] hover:text-[var(--brand-primary)] transition-colors"
                >
                  {t('dpa.title')}
                </Link>
              </li>
              <li>
                <Link
                  to={`/${language}/terms-and-conditions`}
                  className="text-[var(--text-tertiary)] hover:text-[var(--brand-primary)] transition-colors"
                >
                  Terms &amp; Conditions
                </Link>
              </li>
              <li>
                <a href="mailto:contact@invoice-converter.com" className="text-[var(--text-tertiary)] hover:text-[var(--brand-primary)] transition-colors">
                  contact@invoice-converter.com
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="mt-8 pt-8 border-t border-[var(--border-primary)] text-center text-[var(--text-tertiary)]">
          <p>&copy; 2025 Invoice-Converter.com. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;